import { useEffect, useState } from 'react';

/**
 * Layout breakpoints in CSS px. Below DESKTOP_MIN the sidebar becomes a drawer
 * (tablets, and a desktop browser split beside another window); below TABLET_MIN
 * row-grid lists stack into cards.
 */
export const TABLET_MIN = 768;
export const DESKTOP_MIN = 1024;

export type Breakpoint = 'mobile' | 'tablet' | 'desktop';

const DESKTOP_QUERY = `(min-width: ${DESKTOP_MIN}px)`;
const TABLET_QUERY = `(min-width: ${TABLET_MIN}px)`;

// jsdom has no matchMedia, so tests (and SSR) read as desktop — the layout every
// existing test was written against.
function read(): Breakpoint {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'desktop';
  if (window.matchMedia(DESKTOP_QUERY).matches) return 'desktop';
  if (window.matchMedia(TABLET_QUERY).matches) return 'tablet';
  return 'mobile';
}

/**
 * Live match for one media query. `fallback` is the answer where matchMedia
 * doesn't exist (jsdom, SSR) — pass the value the desktop layout implies.
 */
export function useMediaQuery(query: string, fallback: boolean): boolean {
  const read = () =>
    typeof window === 'undefined' || typeof window.matchMedia !== 'function'
      ? fallback
      : window.matchMedia(query).matches;
  const [matches, setMatches] = useState(read);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

export function useBreakpoint(): Breakpoint {
  const [bp, setBp] = useState<Breakpoint>(read);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const queries = [window.matchMedia(DESKTOP_QUERY), window.matchMedia(TABLET_QUERY)];
    const onChange = () => setBp(read());
    queries.forEach((q) => q.addEventListener('change', onChange));
    return () => queries.forEach((q) => q.removeEventListener('change', onChange));
  }, []);

  return bp;
}
